import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, CheckCircle, User, Building, Mail, Phone } from "lucide-react";
import { useLocation } from "react-router-dom";
import { getDemoFromInvite, saveDemoAvailableTimesForInvite } from "../../utils/api";

const DemoRequestForm = () => {
   const search = useLocation().search;
    const [demo, setDemo] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
      let imageUrl = process.env.REACT_APP_IMAGE_URL;
    let tok = new URLSearchParams(search).get('refid');

    useEffect(() => {
    async function loadDemo() {
      let res = await getDemoFromInvite(tok);

      if (res.success) {
        setDemo(res.data);
        setLoading(false);
      } else if (res.status === 403) {
        // Handle token refresh logic here if needed
        console.error("Access denied. Please check your token.");
        navigate("/");
      }
    }

    loadDemo();
  }, []);

  // Pre-populated user data
  const [formData, setFormData] = useState({
    name: "Sarah Johnson",
    email: "sarah.johnson@techcorp.com",
    company: "TechCorp Solutions",
    title: "VP of Engineering",
    phone: "+1 (555) 123-4567",
    timeSlot1: "",
    timeSlot2: "",
    timeSlot3: "",
    notes: ""
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.timeSlot1) {
      return;
    }

    let _times = [];
        _times.push(formData.timeSlot1);
        _times.push(formData.timeSlot2);
        _times.push(formData.timeSlot3);
    
        let s_timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        let res = await saveDemoAvailableTimesForInvite(tok, _times, s_timezone, '');
    
        if (res.success){
            setIsSubmitted(true);
        }
    
        else {
          //alert('nope');
        }

    // Simulate form submission
    console.log("Demo request submitted:", formData);
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center bg-white rounded-lg border border-gray-200 shadow-md">
          <div className="pt-6 px-6 pb-6">
            <div className="mb-6">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Request Submitted!</h2>
              <p className="text-gray-600">
                Thank you for your interest. We'll review your availability and get back to you within 24 hours to confirm your demo session.
              </p>
            </div>
            <div className="space-y-3 text-sm text-gray-500 mb-6">
              <p>• You'll receive a calendar invite once confirmed</p>
              <p>• Demo sessions typically last 30-45 minutes</p>
              <p>• We'll send you preparation materials beforehand</p>
            </div>
            <Link to="/">
              <button className="w-full inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-blue-600 text-white hover:bg-blue-700 h-10 px-4 py-2">
                Return to Home
              </button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className=" mx-auto px-4 py-4 max-w-4xl">
          <div className="flex items-center gap-4">
            <Link 
              to="/" 
              className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </Link>
            <div className="h-6 w-px bg-gray-300"></div>
            <h1 className="text-xl font-semibold text-gray-900">Schedule Your Demo</h1>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {loading == false && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-md mb-8">
          <div className="pt-6 px-6 pb-6">
            <div className="flex items-center gap-6">
              <div className="flex-shrink-0 w-16 h-16">
                    <img 
                      src={imageUrl + 'tr:di-@@companylogos@@default-buyer.png/' + demo.logo}
                      alt={demo?.seller_company_name}
                      className="w-full h-full object-cover rounded-full"
                    />
                  
              </div>
              <div className="flex-grow">
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-2xl font-bold text-gray-900">{demo?.seller_company_name}</h2>
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                    Inviting You
                  </span>
                </div>
                <p className="text-lg text-gray-700 mb-2">{demo?.tagline}</p>
                <p className="text-gray-600">{demo?.description}</p>
              </div>
            </div>
          </div>
        </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg border border-gray-200 shadow-md">
              <div className="flex flex-col space-y-1.5 p-6 border-b border-gray-200">
                <h3 className="text-2xl font-semibold leading-none tracking-tight flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-blue-600" />
                  Demo Request Form
                </h3>
                <p className="text-gray-600">
                  You've been pre-approved for a personalized demo! Please confirm your details and preferred times below.
                </p>
              </div>

              <div className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Profile Section */}
                  {loading == false && (
                  <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">

                    <div>
                      <h3 className="font-semibold text-gray-900">{demo?.first_name} {demo?.last_name}</h3>
                      <p className="text-gray-600">{demo?.job_title}</p>
                      <p className="text-blue-600 font-medium">{demo?.buyer_company_name}</p>
                    </div>
                  </div>
                  )}

                  {/* Time Preferences */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Preferred Demo Times</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Please provide 2-3 time slots that work for you. All times should be in your local timezone.
                    </p>
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="timeSlot1" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                          First Choice *
                        </label>
                        <input
                          id="timeSlot1"
                          name="timeSlot1"
                          type="datetime-local"
                          value={formData.timeSlot1}
                          onChange={handleInputChange}
                          required
                          className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                        />
                      </div>
                      <div>
                        <label htmlFor="timeSlot2" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                          Second Choice
                        </label>
                        <input
                          id="timeSlot2"
                          name="timeSlot2"
                          type="datetime-local"
                          value={formData.timeSlot2}
                          onChange={handleInputChange}
                          className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                        />
                      </div>
                      <div>
                        <label htmlFor="timeSlot3" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                          Third Choice
                        </label>
                        <input
                          id="timeSlot3"
                          name="timeSlot3"
                          type="datetime-local"
                          value={formData.timeSlot3}
                          onChange={handleInputChange}
                          className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  <button 
                    type="submit" 
                    className="w-full inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-blue-600 text-white hover:bg-blue-700 h-10 px-4 py-2"
                  >
                    Submit Demo Request
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* Sidebar Information */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 shadow-md">
              <div className="flex flex-col space-y-1.5 p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold leading-none tracking-tight">What to Expect</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium">30-45 Minutes</h4>
                    <p className="text-sm text-gray-600">Personalized demo tailored to your needs</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <User className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium">Expert Guide</h4>
                    <p className="text-sm text-gray-600">Led by our product specialists</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Building className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium">Custom Scenarios</h4>
                    <p className="text-sm text-gray-600">See how it works with your use cases</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 shadow-md">
              <div className="flex flex-col space-y-1.5 p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold leading-none tracking-tight">Work Hours</h3>
              </div>
              <div className="p-6">
                <p className="text-sm text-gray-600 mb-4">
                  Our demo team is available during the following hours:
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Monday - Friday</span>
                    <span className="text-gray-900 font-medium">9:00 AM - 6:00 PM PST</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Saturday</span>
                    <span className="text-gray-900 font-medium">10:00 AM - 4:00 PM PST</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Sunday</span>
                    <span className="text-gray-500">Closed</span>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-800">
                    <strong>Note:</strong> All demo sessions are scheduled in your local timezone. We'll confirm availability within 24 hours.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 shadow-md">
              <div className="flex flex-col space-y-1.5 p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold leading-none tracking-tight">Need Help?</h3>
              </div>
              <div className="p-6">
                <p className="text-sm text-gray-600 mb-4">
                  Have questions about the demo or need to reschedule?
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-500" />
                    <span>support@leadrpro.com</span>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoRequestForm;